import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RestaurantRoundedIcon from '@mui/icons-material/RestaurantRounded'
import PageContainer from '../components/ui/PageContainer'
import RegistrationFieldInput from '../components/RegistrationFieldInput'
import { EmptyState, LoadingState } from '../components/ui/States'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'
import {
  registrationService,
  type OptionGroupView,
  type RegistrationField,
  type RegistrationView,
} from '../services/registrationService'
import { formatDateRange } from '../utils/format'

type Values = Record<string, string>

function apiMessage(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response
    ?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

export default function RegisterPage() {
  const { isAuthenticated } = useAttendeeAuth()
  const [loading, setLoading] = useState(true)
  const [fields, setFields] = useState<RegistrationField[]>([])
  const [registration, setRegistration] = useState<RegistrationView | null>(null)
  const [groups, setGroups] = useState<OptionGroupView[]>([])
  const [values, setValues] = useState<Values>({})
  const [errors, setErrors] = useState<Record<string, boolean>>({})
  const [submitting, setSubmitting] = useState(false)
  const [banner, setBanner] = useState<{ msg: string; sev: 'success' | 'error' } | null>(
    null,
  )

  const load = async () => {
    const [f, reg, g] = await Promise.all([
      registrationService.getFields(),
      registrationService.getMyRegistration(),
      registrationService.getOptionGroups(),
    ])
    setFields(f)
    setRegistration(reg)
    setGroups(g)
    if (reg.values) setValues(reg.values as Values)
  }

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false)
      return
    }
    load()
      .catch(() => setBanner({ msg: 'Could not load registration.', sev: 'error' }))
      .finally(() => setLoading(false))
  }, [isAuthenticated])

  const requiredFields = useMemo(() => fields.filter((f) => f.required), [fields])
  const isRegistered = registration?.registered ?? false

  const setValue = (fieldId: string, value: string) => {
    setValues((v) => ({ ...v, [fieldId]: value }))
    setErrors((e) => ({ ...e, [fieldId]: false }))
  }

  const handleRegister = async () => {
    const nextErrors: Record<string, boolean> = {}
    for (const f of requiredFields) {
      const v = values[f.id]
      if (v === undefined || v === '') nextErrors[f.id] = true
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }
    setSubmitting(true)
    try {
      await registrationService.register(values)
      await load()
      setBanner({ msg: "You're registered.", sev: 'success' })
    } catch (e) {
      setBanner({ msg: apiMessage(e, 'Registration failed.'), sev: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleSelect = async (groupId: string, optionId: string) => {
    try {
      await registrationService.selectOption(groupId, optionId)
      setGroups(await registrationService.getOptionGroups())
      setBanner({ msg: 'Selection saved.', sev: 'success' })
    } catch (e) {
      setBanner({ msg: apiMessage(e, 'Could not save selection.'), sev: 'error' })
      // Refresh so capacity/availability reflect reality after a conflict.
      setGroups(await registrationService.getOptionGroups())
    }
  }

  const handleUnselect = async (groupId: string) => {
    try {
      await registrationService.unselectOption(groupId)
      setGroups(await registrationService.getOptionGroups())
    } catch (e) {
      setBanner({ msg: apiMessage(e, 'Could not clear selection.'), sev: 'error' })
    }
  }

  if (loading) return <LoadingState />
  if (!isAuthenticated)
    return (
      <PageContainer>
        <EmptyState
          title="Sign in to register"
          description="Sign in to view and complete your event registration."
        />
      </PageContainer>
    )

  return (
    <PageContainer maxWidth="sm">
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Registration
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Confirm your attendance and make your selections.
      </Typography>

      {banner && (
        <Alert severity={banner.sev} sx={{ mb: 2 }} onClose={() => setBanner(null)}>
          {banner.msg}
        </Alert>
      )}

      {/* --- Registration step --- */}
      {isRegistered ? (
        <Card variant="outlined" sx={{ p: 2.5, mb: 4, borderRadius: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleRoundedIcon color="success" />
            <Typography variant="subtitle1" fontWeight={700}>
              You're registered for this event
            </Typography>
          </Stack>
        </Card>
      ) : (
        <Stack spacing={3} sx={{ mb: 4 }}>
          {fields.map((field) => (
            <RegistrationFieldInput
              key={field.id}
              field={field}
              value={values[field.id]}
              error={errors[field.id]}
              onChange={(v) => setValue(field.id, v)}
            />
          ))}
          {Object.values(errors).some(Boolean) && (
            <Alert severity="error">Please complete the required fields.</Alert>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={handleRegister}
            disabled={submitting}
          >
            {submitting ? 'Registering…' : 'Confirm registration'}
          </Button>
        </Stack>
      )}

      {/* --- Option groups (e.g. dinners) --- */}
      {groups.length > 0 && <Divider sx={{ mb: 3 }} />}
      <Stack spacing={4}>
        {groups.map((group) => (
          <OptionGroupSection
            key={group.id}
            group={group}
            canSelect={isRegistered || !group.requiresRegistration}
            onSelect={handleSelect}
            onUnselect={handleUnselect}
          />
        ))}
      </Stack>
    </PageContainer>
  )
}

function OptionGroupSection({
  group,
  canSelect,
  onSelect,
  onUnselect,
}: {
  group: OptionGroupView
  canSelect: boolean
  onSelect: (groupId: string, optionId: string) => void
  onUnselect: (groupId: string) => void
}) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <RestaurantRoundedIcon color="action" fontSize="small" />
        <Typography variant="h6" fontWeight={700}>
          {group.title}
        </Typography>
      </Stack>
      {group.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {group.description}
        </Typography>
      )}
      {group.maxSelect === 1 && (
        <Typography variant="caption" color="text.secondary">
          Choose one.
        </Typography>
      )}

      {!canSelect && (
        <Alert severity="info" sx={{ my: 1.5 }}>
          Complete your registration above to make a selection.
        </Alert>
      )}
      {group.locked && (
        <Alert severity="warning" sx={{ my: 1.5 }}>
          The selection deadline has passed.
        </Alert>
      )}

      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
        {group.options.map((opt) => {
          const disabled =
            !canSelect ||
            group.locked ||
            !opt.eligible ||
            (opt.isFull && !opt.selected)
          // For a single-choice group, once one option is registered, hide the
          // Register button on every other option until it's cleared. The
          // selected option keeps its Clear button so the user can switch.
          const groupHasSelection = group.options.some((o) => o.selected)
          const hideRegister =
            !opt.selected && group.maxSelect === 1 && groupHasSelection
          return (
            <Card
              key={opt.id}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderColor: opt.selected ? 'primary.main' : 'divider',
                bgcolor: opt.selected ? 'rgba(0,117,188,0.06)' : 'transparent',
                opacity: disabled && !opt.selected ? 0.6 : 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {opt.title}
                  </Typography>
                  {opt.selected && (
                    <Chip
                      size="small"
                      color="primary"
                      label="Registered"
                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                    />
                  )}
                  {opt.isFull && !opt.selected && (
                    <Chip
                      size="small"
                      label="Full"
                      sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                    />
                  )}
                  {!opt.eligible && (
                    <Chip
                      size="small"
                      label="Not eligible"
                      sx={{ height: 20, fontSize: '0.68rem' }}
                    />
                  )}
                </Stack>
                {opt.startsAt && opt.endsAt && (
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {formatDateRange(opt.startsAt, opt.endsAt)}
                    {opt.location ? ` · ${opt.location}` : ''}
                  </Typography>
                )}
                {opt.description && (
                  <Typography variant="body2" color="text.secondary">
                    {opt.description}
                  </Typography>
                )}
                {opt.capacity != null && (
                  <Typography variant="caption" color="text.secondary">
                    {opt.remaining} of {opt.capacity} seats left
                  </Typography>
                )}
              </Box>
              {opt.selected ? (
                <Button
                  size="small"
                  color="inherit"
                  disabled={group.locked}
                  onClick={() => onUnselect(group.id)}
                >
                  Clear
                </Button>
              ) : hideRegister ? null : (
                <Button
                  size="small"
                  variant="contained"
                  disabled={disabled}
                  onClick={() => onSelect(group.id, opt.id)}
                >
                  Register
                </Button>
              )}
            </Card>
          )
        })}
      </Stack>
    </Box>
  )
}
